import type { ReactNode } from 'react'
import { Loader } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import { useIsClient } from 'usehooks-ts'
import { useEmbeddedVideo } from 'modules/attachments/api/get-embedded-video'
import type { EmbeddedVideoAttachment } from 'modules/community/types/post-attachment'

interface EmbeddedVideoGalleryPreviewProps {
  attachment: EmbeddedVideoAttachment
}

const EmbeddedVideoGalleryPreview = ({
  attachment,
}: EmbeddedVideoGalleryPreviewProps): ReactNode => {
  const isClient = useIsClient()
  const { t } = useTranslation()
  const metadata = useEmbeddedVideo({
    url: attachment.url,
    enabled: isClient && !!attachment.url,
  })

  if (!attachment.url) return null
  if (metadata.isPending) return <Loader />
  if (metadata.isError)
    return <p data-testid="embedded-video-unavailable">{t('post.attachments.upload_failed')}</p>

  const source = new URL(metadata.data.playback.src)
  if (source.hostname === 'clips.twitch.tv')
    source.searchParams.set('parent', globalThis.location.hostname)

  return (
    <article
      key={attachment.id}
      className="inline-flex size-full shrink-0 snap-center justify-center px-20 py-10 lg:aspect-video lg:p-40"
    >
      {/* eslint-disable-next-line @eslint-react/dom-no-missing-iframe-sandbox */}
      <iframe
        data-testid="embedded-video-player"
        title={metadata.data.title}
        className="size-full border-0"
        src={source.toString()}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </article>
  )
}

export default EmbeddedVideoGalleryPreview
